import CutwiseAPIClient, {
  HEADER_X_TOTAL_COUNT,
  METHOD_COPY,
  METHOD_DELETE,
  METHOD_GET,
  METHOD_PATCH,
  METHOD_POST,
} from '@/common/network/CutwiseAPIClient';
import { ProductId } from '@/product/entity/Product';

import { CollectionItem } from '../entities/DriveItem/CollectionItem';
import { createDriveItem, DriveItem, DriveItemType } from '../entities/DriveItem/DriveItem';
import { DriveContainerId, DriveItemId } from '../entities/DriveItemId';
import { DriveItemsResponse } from '../entities/DriveItemsResponse';
import { DriveFolder, DriveNode } from '../entities/DriveNode';
import { DriveType } from '../entities/DriveType';
import { TransientDriveItem } from '../entities/transientDriveItem';
import { normalizeDriveNode } from '../helpers/normalizeDriveNode';
import { AccessInheritanceResponse } from '../types/AccessInheritanceResponse';
import { CreateCollectionRequestData } from '../types/CreateCollectionRequestData';
import { DriveItemRequest } from '../types/DriveItemRequest';
import { DrivePath } from '../types/DrivePath';
import { UpdateCollectionRequestData } from '../types/UpdateCollectionRequestData';

export const PATH_DRIVE = '/v1/drives';

export class DriveAPI {
  constructor(private driveType: DriveType = DriveType.CutwiseDrive) {}

  private get baseUrl() {
    return `${PATH_DRIVE}/${this.driveType}`;
  }

  private get collectionsUrl() {
    return `${this.baseUrl}/collections`;
  }

  async fetchTree(): Promise<DriveNode[]> {
    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_GET).path(this.baseUrl);

    const res = await CutwiseAPIClient.sendQuery(qb.getQuery());

    return (res.content ?? []).map(normalizeDriveNode);
  }

  async fetchFolder(id: DriveContainerId): Promise<DriveFolder> {
    const qb = CutwiseAPIClient.createQueryBuilder()
      .method(METHOD_GET)
      .path(`${this.baseUrl}/items`)
      .addQueryStringParam('folderId', id);

    const res = await CutwiseAPIClient.sendQuery(qb.getQuery());

    const folder = res.content as DriveFolder;

    return {
      ...folder,
      children: (folder.children ?? [])
        // filtering is a temporary code - we do not support multiproject in drive, their appearance is a bug in the backend
        .filter((item: Any) => item.type !== 'project')
        .map(createDriveItem),
    };
  }

  async fetchPath(nodeId: DriveItemId): Promise<DrivePath> {
    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_GET).path(`${this.baseUrl}/nodes/${nodeId}/path`);

    const res = await CutwiseAPIClient.sendQuery(qb.getQuery());

    return res.content ?? [];
  }

  async fetchLinkedPaths(nodeId: DriveItemId): Promise<DrivePath[]> {
    const qb = CutwiseAPIClient.createQueryBuilder()
      .method(METHOD_GET)
      .path(`${this.baseUrl}/nodes/${nodeId}/linked-paths`);

    const res = await CutwiseAPIClient.sendQuery(qb.getQuery());

    return res.content ?? [];
  }

  async batchUpdate(requests: DriveItemRequest[]): Promise<DriveItemsResponse> {
    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_PATCH).body(requests).path(this.baseUrl);

    const res = await CutwiseAPIClient.sendQuery(qb.getQuery());

    return DriveItemsResponse.fromDriveItems(res.content);
  }

  async batchCreate(requests: DriveItemRequest[]): Promise<DriveItemsResponse> {
    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_POST).body(requests).path(this.baseUrl);

    const res = await CutwiseAPIClient.sendQuery(qb.getQuery());

    return DriveItemsResponse.fromDriveItems(res.content);
  }

  async deleteById(id: DriveItemId): Promise<boolean> {
    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_DELETE).body([{ id }]).path(this.baseUrl);

    const res = await CutwiseAPIClient.sendQuery(qb.getQuery());

    return !new DriveItemsResponse(Array.isArray(res.content) ? res.content : []).hasErrors();
  }

  async makeDto(requests: DriveItemRequest[]): Promise<DriveItemsResponse<TransientDriveItem>> {
    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_POST).body(requests).path(`${this.baseUrl}/dto`);

    const res = await CutwiseAPIClient.sendQuery(qb.getQuery());

    return DriveItemsResponse.fromDriveItems(
      res.content ?? [],
      createDriveItem,
    ) as DriveItemsResponse<TransientDriveItem>;
  }

  async createCollection(reqData: CreateCollectionRequestData): Promise<DriveItemsResponse<CollectionItem>> {
    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_POST).body(reqData).path(this.collectionsUrl);

    const res = await CutwiseAPIClient.sendQuery(qb.getQuery());

    return DriveItemsResponse.fromDriveItems([res.content], (item) => new CollectionItem(item));
  }

  async updateCollection(
    collectionNodeId: DriveItemId,
    reqData: UpdateCollectionRequestData,
  ): Promise<DriveItemsResponse> {
    const node: Record<string, unknown> = {
      id: collectionNodeId,
      title: reqData.title,
      description: reqData.description,
      inheritAccess: false,
    };

    if (reqData.settings) {
      node.collectionSettings = reqData.settings;
    }

    const qb = CutwiseAPIClient.createQueryBuilder().method(METHOD_PATCH).body([node]).path(this.baseUrl);

    const res = await CutwiseAPIClient.sendQuery(qb.getQuery());

    return DriveItemsResponse.fromDriveItems(res.content);
  }

  async subscribeToCollection(
    collectionNodeId: DriveItemId,
    destinationFolderId: DriveContainerId,
  ): Promise<DriveItemsResponse<CollectionItem>> {
    const qb = CutwiseAPIClient.createQueryBuilder()
      .method(METHOD_POST)
      .body({ destinationFolderId })
      .path(`${this.collectionsUrl}/${collectionNodeId}/subscribe`)
      .suppressErrorNotifications();

    const res = await CutwiseAPIClient.sendQuery(qb.getQuery());

    return DriveItemsResponse.fromDriveItems([res.content], (item) => new CollectionItem(item));
  }

  async materializeCollectionSubscription(subscriptionNodeId: DriveItemId, destinationFolderId: DriveContainerId) {
    const qb = CutwiseAPIClient.createQueryBuilder()
      .method(METHOD_POST)
      .body({ destinationFolderId })
      .path(`${this.baseUrl}/nodes/${subscriptionNodeId}/materialize-collection-subscription`)
      .suppressErrorNotifications();

    const res = await CutwiseAPIClient.sendQuery(qb.getQuery());

    return DriveItemsResponse.fromDriveItems([res.content], (item) => new CollectionItem(item));
  }

  async copyDriveItem(
    id: DriveItemId,
    destinationFolderId: DriveContainerId,
    form: { title: Nullable<string>; description: Nullable<string> },
    shouldInheritAccess: boolean,
    precedingNodeId?: DriveItemId,
  ): Promise<DriveItemsResponse> {
    const qb = CutwiseAPIClient.createQueryBuilder()
      .method(METHOD_COPY)
      .body([{ id, destinationFolderId, inheritAccess: shouldInheritAccess, precedingNodeId, ...form }])
      .path(this.baseUrl);

    const res = await CutwiseAPIClient.sendQuery(qb.getQuery());

    return DriveItemsResponse.fromDriveItems(res.content);
  }

  async moveToCollection(
    destinationNodeId: DriveItemId,
    driveIds: DriveItemId[],
    shouldInheritAccess: boolean,
    { suppressErrorNotifications = false }: { suppressErrorNotifications?: boolean } = {},
  ): Promise<AccessInheritanceResponse[]> {
    const qb = CutwiseAPIClient.createQueryBuilder()
      .method(METHOD_POST)
      .body({ productNodeIds: driveIds, inheritAccess: shouldInheritAccess })
      .path(`${this.collectionsUrl}/${destinationNodeId}/move-products`);

    if (suppressErrorNotifications) {
      qb.suppressErrorNotifications();
    }

    const res = await CutwiseAPIClient.sendQuery(qb.getQuery());

    return res.content ?? [];
  }

  async addToCollection(
    destinationNodeId: DriveItemId,
    productIds: DriveItemId[],
    shouldInheritAccess: boolean,
    { suppressErrorNotifications = false }: { suppressErrorNotifications?: boolean } = {},
  ): Promise<AccessInheritanceResponse[]> {
    const qb = CutwiseAPIClient.createQueryBuilder()
      .method(METHOD_POST)
      .body({ productIds, inheritAccess: shouldInheritAccess })
      .path(`${this.collectionsUrl}/${destinationNodeId}/add-products`);

    if (suppressErrorNotifications) {
      qb.suppressErrorNotifications();
    }

    const res = await CutwiseAPIClient.sendQuery(qb.getQuery());

    return res.content ?? [];
  }

  async removeFromCollection(collectionNodeId: DriveItemId, productIds: ProductId[]): Promise<void> {
    const qb = CutwiseAPIClient.createQueryBuilder()
      .method(METHOD_DELETE)
      .body({ productIds })
      .path(`${this.collectionsUrl}/${collectionNodeId}/remove-products`);

    await CutwiseAPIClient.sendQuery(qb.getQuery());
  }

  async sortCollectionProducts(collectionNodeId: DriveItemId, productIds: number[]): Promise<void> {
    const qb = CutwiseAPIClient.createQueryBuilder()
      .method(METHOD_PATCH)
      .body({ productIds })
      .path(`${this.collectionsUrl}/${collectionNodeId}/sort`);

    await CutwiseAPIClient.sendQuery(qb.getQuery());
  }

  async reorder(destinationFolderId: DriveContainerId, orderedDriveNodeIds: DriveItemId[]) {
    const qb = CutwiseAPIClient.createQueryBuilder()
      .method(METHOD_PATCH)
      .body({ destinationFolderId, orderedDriveNodeIds })
      .path(`${this.baseUrl}/reorder`);

    await CutwiseAPIClient.sendQuery(qb.getQuery());
  }

  async search(
    searchQuery: string,
    itemType: Nullable<DriveItemType> = null,
    offset: Nullable<number> = null,
    limit: Nullable<number> = null,
  ): Promise<{ totalCount: number; items: DriveItem[] }> {
    const qb = CutwiseAPIClient.createQueryBuilder()
      .method(METHOD_GET)
      .path(`${this.baseUrl}/search`)
      .offset(offset)
      .limit(limit)
      .addQueryStringParam('query', searchQuery);

    if (itemType) {
      qb.addQueryStringParam('driveNodeType', itemType);
    }

    const res = await CutwiseAPIClient.sendQuery(qb.getQuery());

    const totalCountStr = res.networkResponse?.headers.get(HEADER_X_TOTAL_COUNT);

    return {
      items: res.content.map(createDriveItem),
      totalCount: totalCountStr ? parseInt(totalCountStr, 10) : 0,
    };
  }
}
